import { DASHBOARD_RANGES, consoleRoutes, type DashboardRange } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, EmptyState, ErrorState, Icon, Segmented, Skeleton } from "@sagot/ui";
import { useState } from "react";
import { useSearchParams } from "react-router";
import { usePermissions } from "../../../session/permissions";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { useFormatters } from "../shared/format";
import { ActivityCards, ActivitySkeleton } from "./activity-cards";
import { firstName, greetingKey, hourIn, isDashboardRange } from "./dashboard-model";
import { KpiRow, KpiRowSkeleton } from "./kpi-row";
import { RecentCards } from "./recent-cards";
import { SetupChecklist } from "./setup-checklist";
import { ServiceDashboardCard } from "./service-card";
import { ShareDialog } from "./share-dialog";
import { useAgentList } from "../shared/use-agent-list";
import { useDashboard, useSetupChecklist } from "./use-dashboard";

const RANGE_PARAM = "range";

function DashboardHeader({ range, onRange, agent }: { range: DashboardRange; onRange: (r: DashboardRange) => void; agent: { id: string; name: string } | null }) {
  const { t } = useTranslation();
  const { summary, me } = useWorkspaceContext();
  const permissions = usePermissions();
  const format = useFormatters();
  const [sharing, setSharing] = useState(false);
  const greeting = t(`dashboard.greeting.${greetingKey(hourIn(format.timeZone))}`, { name: firstName(me.user.name) });
  const period = t(`dashboard.period.${range}`);
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight">{greeting}</h1>
        <p className="mt-1 text-fg-muted">
          {agent ? (
            <>
              {t("dashboard.subtitleAgentLead")} <strong className="text-fg">{agent.name}</strong> {t("dashboard.subtitleAgentTail", { period })}
            </>
          ) : (
            t("dashboard.subtitleAll", { period })
          )}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Segmented aria-label={t("dashboard.range.label")} value={range} onValueChange={onRange} options={DASHBOARD_RANGES.map((value) => ({ value, label: t(`common.timeRange.${value}`) }))} />
        {agent && permissions.can("playground.use", { anywhere: true }) ? (
          <Button asChild variant="secondary">
            <AppLink href={consoleRoutes.playground(summary.slug, agent.id)}>
              <Icon name="messages-square" />
              {t("dashboard.testInPlayground")}
            </AppLink>
          </Button>
        ) : null}
        {agent ? (
          <Button onClick={() => { setSharing(true); }}>
            <Icon name="link" />
            {t("dashboard.shareLink")}
          </Button>
        ) : null}
      </div>
      {agent ? <ShareDialog agent={agent} open={sharing} onOpenChange={setSharing} /> : null}
    </div>
  );
}

function NoAgents() {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  return (
    <div className="card">
      <EmptyState
        icon="bot"
        title={t("dashboard.noAgentsTitle")}
        description={t("dashboard.noAgentsBody")}
        action={
          <Button asChild>
            <AppLink href={`${consoleRoutes.agents(summary.slug)}?new=1`}>
              <Icon name="plus" />
              {t("dashboard.createAgent")}
            </AppLink>
          </Button>
        }
      />
    </div>
  );
}

function DashboardBody({ range, agentId }: { range: DashboardRange; agentId: string | null }) {
  const { t } = useTranslation();
  const format = useFormatters();
  const dashboard = useDashboard(range, agentId);
  const checklist = useSetupChecklist();
  if (dashboard.isError) {
    return (
      <div className="card">
        <ErrorState title={t("dashboard.loadError")} description={t("dashboard.loadErrorBody")} onRetry={() => void dashboard.refetch()} retryLabel={t("common.retry")} />
      </div>
    );
  }
  return (
    <>
      {checklist.data ? <SetupChecklist checklist={checklist.data} agentId={agentId} /> : null}
      {dashboard.isPending ? (
        <>
          <KpiRowSkeleton />
          <ActivitySkeleton />
          <Skeleton className="h-72 w-full" />
        </>
      ) : (
        <div className="space-y-6" aria-busy={dashboard.isFetching}>
          <KpiRow kpis={dashboard.data.kpis} range={range} formatNumber={format.number} />
          <ActivityCards data={dashboard.data} format={format} />
          <RecentCards data={dashboard.data} format={format} agentId={agentId} />
        </div>
      )}
    </>
  );
}

/** `/w/:ws/dashboard` (tenant/dashboard.html): the primary (first live) agent's KPIs for the chosen period. */
export function DashboardPage() {
  const [params, setParams] = useSearchParams();
  const agents = useAgentList();
  const raw = params.get(RANGE_PARAM);
  const range: DashboardRange = isDashboardRange(raw) ? raw : "7d";
  const list = agents.data ?? [];
  const primary = list.find((a) => a.status === "live") ?? list[0];
  const onRange = (next: DashboardRange) => {
    setParams((current) => {
      const updated = new URLSearchParams(current);
      updated.set(RANGE_PARAM, next);
      return updated;
    }, { replace: true });
  };
  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <DashboardHeader range={range} onRange={onRange} agent={primary ? { id: primary.id, name: primary.name } : null} />
      {agents.isSuccess && list.length === 0 ? <NoAgents /> : <DashboardBody range={range} agentId={primary?.id ?? null} />}
      <ServiceDashboardCard />
    </div>
  );
}
